import { get, isArray, isFunction, isString } from "lodash-es";
import React, { ComponentType, PureComponent } from "react";

import { ISelectProps } from "./components/types";

export interface IStructure {
  label: string;
  children: IStructure[];
}

export const prepareNestedNames = <T extends IStructure>(structure: T[], prefix = ""): T[] =>
  structure.map(item => ({
    ...item,
    label: `${prefix}${item.label}`,
    children: item.children?.length ? prepareNestedNames(item.children, prefix + "   ") : [],
  }));

// TODO move it
type Finder = (elem: any) => any;
export const flatBy = (arr: any[] = [], keyProp: string | Finder) => {
  return arr.reduce((flattedList, item) => {
    flattedList.push(item);

    let children;
    if (isString(keyProp)) {
      children = get(item, keyProp);
    }

    if (isFunction(keyProp)) {
      children = keyProp(item);
    }

    if (isArray(children) && children.length) {
      flattedList = [...flattedList, ...flatBy(children, keyProp)];
    }

    return flattedList;
  }, []);
};

export function withChildren<P = {}>(Component: ComponentType<P>): ComponentType<P> {
  interface IWithChildrenProps extends ISelectProps {
    childrenKey?: string;
  }

  type Props = IWithChildrenProps & P;

  return class WithChildren extends PureComponent<Props> {
    static defaultProps: Partial<Props> = {
      childrenKey: "children",
      delimiter: ",",
    } as any;

    selected: any[];

    constructor(props: Props) {
      super(props);
      this.selected = this.findSelectedOptions(props);
    }

    componentDidUpdate() {
      this.selected = this.findSelectedOptions(this.props);
    }

    findSelectedOptions = (props: Props) => {
      if (!props.value) {
        return [];
      }

      if (!isArray(props.options)) {
        return [];
      }

      const values = props.value.toString().split(props.delimiter) as string[];
      return props.options.filter(o => values.includes(this.getValueFrom(o).toString()));
    };

    withChildrenValues = (value: any[]) => {
      const childrenKey = this.props.childrenKey as string;

      const missingElements = this.selected.filter(elem => !value.includes(elem));
      const missingElementsWithChildren = flatBy(missingElements, childrenKey);

      return value.reduce((flattedList, element) => {
        if (!missingElementsWithChildren.includes(element)) {
          flattedList.push(element);
        }

        if (isArray(element[childrenKey]) && element[childrenKey].length && !this.selected.includes(element)) {
          const allChildren = flatBy(element[childrenKey], childrenKey);
          const notYetAdded = allChildren.filter((elem: any) => !this.selected.includes(elem));

          flattedList = [...flattedList, ...notYetAdded];
        }

        return flattedList;
      }, []);
    };

    getValueFrom = (target: any) => (target && target[this.props.valueKey as string]) || target;

    prepareValue = (value: any = []) => {
      return this.withChildrenValues(value ? value : []);
    };

    onChange = (value: any) => {
      const { onChange } = this.props;

      if (onChange) {
        onChange(this.prepareValue(value));
      }
    };

    render() {
      const { disabled, readonly, multi, onChange, options, removeSelected, size, value, ...rest } = this.props as any;

      return (
        <Component
          disabled={disabled || readonly}
          onChange={this.onChange}
          value={value}
          options={options}
          removeSelected={false}
          multi={true}
          {...rest}
        />
      );
    }
  };
}
